import { ApplicationInsights } from "@microsoft/applicationinsights-web"
import { onLCP, onINP, onCLS, onFCP, onTTFB } from "web-vitals"
import type { Metric } from "web-vitals"

let appInsights: ApplicationInsights | null = null

// ---------------------------------------------------------------------------
// SPA route-transition state
// ---------------------------------------------------------------------------
let routeTransitionStart: number | null = null
let previousRoute: string | null = null
/** Tracks whether the very first Page mount has occurred (initial full-page load) */
let isInitialPageLoad = true

// ---------------------------------------------------------------------------
// Public helpers – called from other modules (router, ApiService, Page, etc.)
// ---------------------------------------------------------------------------

/**
 * Call at the start of an SPA route transition (inside router.navigate).
 * Records a high-resolution timestamp so we can measure the full transition.
 */
export function markRouteTransitionStart(fromRoute: string): void {
    try {
        routeTransitionStart = performance.now()
        previousRoute = fromRoute
    } catch (_e) {
        // Silently ignore
    }
}

/**
 * Call once the destination page has mounted / rendered (from Page component).
 * Emits `spa_route_transition_ms` with from/to context and records a
 * `trackPageView` with the real measured duration so that the App Insights
 * "Top Pages by View Count" tile shows non-zero AvgDuration_ms.
 *
 * For the initial full-page load (no prior SPA navigate), we record a
 * pageView using Navigation Timing as the duration source.
 */
export function markRouteTransitionEnd(toRoute: string): void {
    if (!appInsights) return
    try {
        if (isInitialPageLoad) {
            // First Page mount after full-page load — use Navigation Timing
            isInitialPageLoad = false
            const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[]
            const loadDuration =
                navEntries.length > 0 && navEntries[0].loadEventEnd > 0
                    ? navEntries[0].loadEventEnd - navEntries[0].fetchStart
                    : performance.now() // fallback: time since page start
            appInsights.trackPageView({
                name: document.title,
                uri: window.location.href,
                properties: {
                    route: toRoute,
                    navigationType: "initial",
                },
                measurements: {
                    duration: loadDuration,
                },
            })
            return
        }

        // SPA navigation — we have a transition start mark
        if (routeTransitionStart === null) return
        const duration = performance.now() - routeTransitionStart

        appInsights.trackMetric(
            { name: "spa_route_transition_ms", average: duration },
            {
                fromRoute: previousRoute ?? "unknown",
                toRoute,
            }
        )

        // Record a pageView with the measured SPA transition duration
        appInsights.trackPageView({
            name: document.title,
            uri: window.location.href,
            properties: {
                route: toRoute,
                fromRoute: previousRoute ?? "unknown",
                navigationType: "spa",
            },
            measurements: {
                duration,
            },
        })

        routeTransitionStart = null
        previousRoute = null
    } catch (_e) {
        // Silently ignore
    }
}

/**
 * Track how long a page/component takes to become "ready" — i.e. first
 * meaningful render after data arrives. Call from ViewModel or component
 * once the initial data load completes.
 *
 * @param pageName  Friendly label, e.g. "WorkOrders", "Dashboard"
 * @param startMark High-resolution timestamp captured at component mount
 */
export function trackPageReady(pageName: string, startMark: number): void {
    if (!appInsights) return
    try {
        const duration = performance.now() - startMark
        appInsights.trackMetric(
            { name: "page_ready_ms", average: duration },
            { page: pageName, route: window.location.pathname }
        )
    } catch (_e) {
        // Silently ignore
    }
}

/**
 * Track the duration of an API call. Wraps a single fetch with timing
 * and emits `api_call_ms` plus success/failure context.
 *
 * @param endpoint  The API path, e.g. "/workorders"
 * @param method    HTTP method
 * @param durationMs  Duration of the call in milliseconds
 * @param success   Whether the call succeeded
 * @param statusCode  HTTP status code (0 if network error)
 */
export function trackApiCall(
    endpoint: string,
    method: string,
    durationMs: number,
    success: boolean,
    statusCode: number
): void {
    if (!appInsights) return
    try {
        appInsights.trackMetric(
            { name: "api_call_ms", average: durationMs },
            {
                endpoint,
                method,
                success: String(success),
                statusCode: String(statusCode),
                route: window.location.pathname,
            }
        )

        // Also track API errors as their own metric for alerting
        if (!success) {
            appInsights.trackMetric(
                { name: "api_error_count", average: 1 },
                {
                    endpoint,
                    method,
                    statusCode: String(statusCode),
                    route: window.location.pathname,
                }
            )
        }
    } catch (_e) {
        // Silently ignore
    }
}

/**
 * Track a custom named metric with optional properties.
 * Convenience wrapper for ad-hoc instrumentation.
 */
export function trackCustomMetric(name: string, value: number, properties?: Record<string, string>): void {
    if (!appInsights) return
    try {
        appInsights.trackMetric({ name, average: value }, { route: window.location.pathname, ...properties })
    } catch (_e) {
        // Silently ignore
    }
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize Application Insights + Web Vitals telemetry.
 * Safe no-op if APPINSIGHTS_CONNECTION_STRING is missing or empty.
 * Never throws — all errors are silently caught.
 */
export function initTelemetry(): void {
    try {
        const connectionString = process.env.APPINSIGHTS_CONNECTION_STRING
        if (!connectionString) return

        appInsights = new ApplicationInsights({
            config: {
                connectionString,
                // Disabled — we manually call trackPageView in markRouteTransitionEnd()
                // with the real measured duration. The auto tracker fires at navigation
                // start and always records duration: 0.
                enableAutoRouteTracking: false,
                disableFetchTracking: false,
                disableAjaxTracking: false,
                enableCorsCorrelation: false,
                disableExceptionTracking: false,
                autoTrackPageVisitTime: true,
                maxBatchSizeInBytes: 10000,
                maxBatchInterval: 15000,
                enableUnhandledPromiseRejectionTracking: true,
            },
        })

        appInsights.loadAppInsights()

        appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.data) {
                const appVersion = process.env.APP_VERSION
                if (appVersion) {
                    envelope.tags = envelope.tags ?? []
                    envelope.tags["ai.application.ver"] = appVersion
                }
            }
            if (envelope.baseData) {
                envelope.baseData.properties = {
                    ...envelope.baseData.properties,
                    env: process.env.NODE_ENV ?? "unknown",
                    route: window.location.pathname,
                }
            }
        })

        initWebVitals()
        initNavigationTiming()
        initLongTaskObserver()
        initResourceTimingObserver()
        initConnectionMetrics()
        initMemoryMetrics()
        initErrorTracking()
    } catch (_e) {
        // Telemetry must never crash the app
    }
}

// ---------------------------------------------------------------------------
// Web Vitals
// ---------------------------------------------------------------------------

function trackVital(metric: Metric): void {
    if (!appInsights) return
    try {
        appInsights.trackMetric(
            { name: `webvitals_${metric.name}`, average: metric.value },
            {
                id: metric.id,
                rating: metric.rating,
                navigationType: metric.navigationType,
                route: window.location.pathname,
            }
        )
    } catch (_e) {
        // Silently ignore
    }
}

function initWebVitals(): void {
    try {
        onLCP(trackVital)
        onINP(trackVital)
        onCLS(trackVital)
        onFCP(trackVital)
        onTTFB(trackVital)
    } catch (_e) {
        // Silently ignore
    }
}

// ---------------------------------------------------------------------------
// Navigation Timing (full-page load)
// ---------------------------------------------------------------------------

function initNavigationTiming(): void {
    if (!appInsights) return
    try {
        const captureNavTiming = () => {
            try {
                const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[]
                if (entries.length === 0) return

                const nav = entries[0]

                // Comprehensive navigation breakdown
                const metrics: Record<string, number> = {
                    // Existing
                    nav_domContentLoaded_ms: nav.domContentLoadedEventEnd,
                    nav_loadEventEnd_ms: nav.loadEventEnd,
                    nav_responseStart_ms: nav.responseStart,
                    // New: DNS, TCP, TLS, request/response, DOM processing
                    nav_dns_ms: nav.domainLookupEnd - nav.domainLookupStart,
                    nav_tcp_ms: nav.connectEnd - nav.connectStart,
                    nav_tls_ms: nav.secureConnectionStart > 0 ? nav.connectEnd - nav.secureConnectionStart : 0,
                    nav_request_ms: nav.responseStart - nav.requestStart,
                    nav_response_ms: nav.responseEnd - nav.responseStart,
                    nav_domProcessing_ms: nav.domComplete - nav.responseEnd,
                    nav_domInteractive_ms: nav.domInteractive - nav.fetchStart,
                    nav_totalLoad_ms: nav.loadEventEnd - nav.fetchStart,
                    // Transfer size for the document itself
                    nav_transferSize_bytes: nav.transferSize,
                    nav_decodedBodySize_bytes: nav.decodedBodySize,
                }

                for (const [name, value] of Object.entries(metrics)) {
                    if (value > 0) {
                        appInsights!.trackMetric({ name, average: value }, { route: window.location.pathname })
                    }
                }
            } catch (_e) {
                // Silently ignore
            }
        }

        if (document.readyState === "complete") {
            captureNavTiming()
        } else {
            window.addEventListener("load", () => {
                setTimeout(captureNavTiming, 0)
            })
        }
    } catch (_e) {
        // Silently ignore
    }
}

// ---------------------------------------------------------------------------
// Long Task Observer — tracks JS execution > 50ms that blocks the main thread
// ---------------------------------------------------------------------------

function initLongTaskObserver(): void {
    if (!appInsights) return
    if (typeof PerformanceObserver === "undefined") return

    try {
        const observer = new PerformanceObserver((list) => {
            try {
                for (const entry of list.getEntries()) {
                    appInsights!.trackMetric(
                        { name: "longtask_duration_ms", average: entry.duration },
                        {
                            route: window.location.pathname,
                            startTime: String(Math.round(entry.startTime)),
                        }
                    )
                }
            } catch (_e) {
                // Silently ignore
            }
        })

        observer.observe({ type: "longtask", buffered: true })
    } catch (_e) {
        // PerformanceObserver for longtask not supported — silently ignore
    }
}

// ---------------------------------------------------------------------------
// Resource Timing Observer — track slow third-party or large resources
// ---------------------------------------------------------------------------

const SLOW_RESOURCE_THRESHOLD_MS = 1000

function initResourceTimingObserver(): void {
    if (!appInsights) return
    if (typeof PerformanceObserver === "undefined") return

    try {
        const observer = new PerformanceObserver((list) => {
            try {
                for (const entry of list.getEntries()) {
                    const resourceEntry = entry as PerformanceResourceTiming
                    // Only report resources that took longer than the threshold
                    if (resourceEntry.duration < SLOW_RESOURCE_THRESHOLD_MS) continue

                    appInsights!.trackMetric(
                        { name: "slow_resource_ms", average: resourceEntry.duration },
                        {
                            resourceName: truncateUrl(resourceEntry.name),
                            initiatorType: resourceEntry.initiatorType,
                            transferSize: String(resourceEntry.transferSize),
                            route: window.location.pathname,
                        }
                    )
                }
            } catch (_e) {
                // Silently ignore
            }
        })

        observer.observe({ type: "resource", buffered: false })
    } catch (_e) {
        // PerformanceObserver for resource not supported — silently ignore
    }
}

// ---------------------------------------------------------------------------
// Connection-aware metrics (Network Information API)
// ---------------------------------------------------------------------------

interface NavigatorConnection {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
}

function initConnectionMetrics(): void {
    if (!appInsights) return
    try {
        const nav = navigator as Navigator & { connection?: NavigatorConnection }
        const conn = nav.connection
        if (!conn) return

        const properties: Record<string, string> = {}
        if (conn.effectiveType) properties.effectiveType = conn.effectiveType
        if (conn.downlink !== undefined) properties.downlink_mbps = String(conn.downlink)
        if (conn.rtt !== undefined) properties.rtt_ms = String(conn.rtt)
        if (conn.saveData !== undefined) properties.saveData = String(conn.saveData)

        appInsights.trackMetric(
            { name: "connection_rtt_ms", average: conn.rtt ?? 0 },
            { route: window.location.pathname, ...properties }
        )
    } catch (_e) {
        // Network Information API not supported — silently ignore
    }
}

// ---------------------------------------------------------------------------
// Memory metrics (performance.memory — Chrome only)
// ---------------------------------------------------------------------------

interface PerformanceMemory {
    jsHeapSizeLimit: number
    totalJSHeapSize: number
    usedJSHeapSize: number
}

function initMemoryMetrics(): void {
    if (!appInsights) return
    try {
        const perf = performance as Performance & { memory?: PerformanceMemory }
        if (!perf.memory) return

        // Capture after initial load
        const capture = () => {
            try {
                if (!perf.memory) return
                appInsights!.trackMetric(
                    { name: "js_heap_used_mb", average: perf.memory.usedJSHeapSize / (1024 * 1024) },
                    {
                        heapLimit_mb: String(Math.round(perf.memory.jsHeapSizeLimit / (1024 * 1024))),
                        heapTotal_mb: String(Math.round(perf.memory.totalJSHeapSize / (1024 * 1024))),
                        route: window.location.pathname,
                    }
                )
            } catch (_e) {
                // Silently ignore
            }
        }

        // Capture once after page load, then periodically (every 60s)
        if (document.readyState === "complete") {
            setTimeout(capture, 1000)
        } else {
            window.addEventListener("load", () => setTimeout(capture, 1000))
        }

        setInterval(capture, 60_000)
    } catch (_e) {
        // performance.memory not supported — silently ignore
    }
}

// ---------------------------------------------------------------------------
// Unhandled error / rejection tracking
// ---------------------------------------------------------------------------

function initErrorTracking(): void {
    if (!appInsights) return
    try {
        window.addEventListener("error", (event: ErrorEvent) => {
            try {
                appInsights!.trackMetric(
                    { name: "unhandled_error_count", average: 1 },
                    {
                        message: truncateString(event.message, 200),
                        filename: event.filename ? truncateUrl(event.filename) : "unknown",
                        line: String(event.lineno ?? 0),
                        col: String(event.colno ?? 0),
                        route: window.location.pathname,
                    }
                )
            } catch (_e) {
                // Silently ignore
            }
        })

        window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
            try {
                const reason = event.reason instanceof Error ? event.reason.message : String(event.reason ?? "unknown")

                appInsights!.trackMetric(
                    { name: "unhandled_rejection_count", average: 1 },
                    {
                        reason: truncateString(reason, 200),
                        route: window.location.pathname,
                    }
                )
            } catch (_e) {
                // Silently ignore
            }
        })
    } catch (_e) {
        // Silently ignore
    }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Truncate a URL to a reasonable length for telemetry properties */
function truncateUrl(url: string): string {
    try {
        const parsed = new URL(url)
        return `${parsed.pathname}${parsed.search}`.slice(0, 200)
    } catch {
        return url.slice(0, 200)
    }
}

/** Truncate a string to maxLen characters */
function truncateString(str: string, maxLen: number): string {
    return str.length > maxLen ? str.slice(0, maxLen) + "..." : str
}
